import { z } from 'zod';
import { periodMonthSchema, periodYearSchema } from './period.js';

/**
 * SFTP drop-folder ingestion (enterprise). An external SFTP host (NOT a Worker —
 * Workers can't listen on port 22) lands files into the account's R2 inbox prefix;
 * this module is the carrier-agnostic, CONFIG-ONLY contract that maps a dropped
 * filename to (carrier, workspace, period). Adding a carrier/route is a new rule,
 * never a code change.
 *
 * Per the product rule: every account maps each file to a workspace + carrier, and
 * the period is taken FROM the filename. If the month cannot be determined the file
 * is left UNPROCESSED (we never guess a period).
 */

/** One mapping rule: a filename regex (with named `year`/`month` groups, or a
 * `period` group of YYYY-MM / YYYYMM) → an explicit carrier (+ optional workspace). */
export const sftpMappingRuleSchema = z.object({
  /** JS regex source matched against the filename (case-insensitive). */
  pattern: z.string().min(1).max(300),
  /** The carrier this rule routes to (carrier is explicit per the product rule). */
  carrierId: z.string().min(1),
  /** Workspace override; falls back to the config's defaultWorkspaceId. */
  workspaceId: z.string().nullish(),
});
export type SftpMappingRule = z.infer<typeof sftpMappingRuleSchema>;

export const sftpConfigSchema = z.object({
  enabled: z.boolean().optional().default(true),
  /** Workspace used when a rule doesn't specify one. */
  defaultWorkspaceId: z.string().nullish(),
  /** What to do when a dropped file targets a carrier+period that already exists. */
  replaceMode: z.enum(['reject', 'replace']).optional().default('reject'),
  rules: z.array(sftpMappingRuleSchema).max(100).default([]),
});
export type SftpConfig = z.infer<typeof sftpConfigSchema>;

export type SftpMapResult =
  | {
      ok: true;
      carrierId: string;
      workspaceId: string | null;
      periodYear: number;
      periodMonth: number;
    }
  | { ok: false; reason: 'no_rule_match' | 'period_undetermined' | 'workspace_unresolved' };

/** Normalise a captured year that may be 2- or 4-digit (`26` → `2026`). */
function normYear(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n)) return null;
  const year = raw.length <= 2 ? 2000 + n : n;
  return periodYearSchema.safeParse(year).success ? year : null;
}

function normMonth(raw: string): number | null {
  const n = Number(raw);
  return periodMonthSchema.safeParse(n).success ? n : null;
}

/**
 * Extract (year, month) from a regex match's named groups. Supports either
 * `year` + `month`, or a single `period` group of `YYYY-MM` / `YYYYMM` / `YYYY_MM`.
 * Returns null when the month can't be determined (→ leave the file unprocessed).
 */
function periodFromGroups(groups: Record<string, string>): { year: number; month: number } | null {
  if (groups.period) {
    const m = /^(\d{4})[-_]?(\d{2})$/.exec(groups.period.trim());
    if (!m?.[1] || !m[2]) return null;
    const year = normYear(m[1]);
    const month = normMonth(m[2]);
    return year && month ? { year, month } : null;
  }
  if (groups.year && groups.month) {
    const year = normYear(groups.year);
    const month = normMonth(groups.month);
    return year && month ? { year, month } : null;
  }
  return null;
}

/**
 * Email-ingest helper: apply ONE filename regex (named `year`+`month`, or a single
 * `period` group) to a filename → `{year, month}` | null. The carrier/workspace come
 * from the inbound alias, so only the period needs reading. Reuses the SFTP rules
 * (2/4-digit year, month validation, no guessing). Pure.
 */
export function periodFromFilenameRegex(
  regexSource: string,
  filename: string,
): { year: number; month: number } | null {
  let re: RegExp;
  try {
    re = new RegExp(regexSource, 'i');
  } catch {
    return null;
  }
  const match = re.exec(filename.trim());
  if (!match) return null;
  return periodFromGroups(match.groups ?? {});
}

/**
 * Map a dropped filename to (carrier, workspace, period) using the account's rules.
 * Pure & deterministic — no I/O. The caller resolves the workspace requirement
 * (when workspaces are enabled, a null workspace is `workspace_unresolved`).
 */
export function parseSftpFilename(
  filename: string,
  config: SftpConfig,
  opts: { workspacesEnabled?: boolean } = {},
): SftpMapResult {
  const name = filename.trim();
  for (const rule of config.rules) {
    let re: RegExp;
    try {
      re = new RegExp(rule.pattern, 'i');
    } catch {
      continue; // a malformed rule pattern is skipped, not fatal
    }
    const match = re.exec(name);
    if (!match) continue;

    const period = periodFromGroups(match.groups ?? {});
    // Rule matched but the month/period isn't in the name → leave unprocessed.
    if (!period) return { ok: false, reason: 'period_undetermined' };

    const workspaceId = rule.workspaceId ?? config.defaultWorkspaceId ?? null;
    // When workspaces are enabled a target workspace MUST resolve (no guessing).
    if (opts.workspacesEnabled && !workspaceId) {
      return { ok: false, reason: 'workspace_unresolved' };
    }
    return {
      ok: true,
      carrierId: rule.carrierId,
      workspaceId,
      periodYear: period.year,
      periodMonth: period.month,
    };
  }
  return { ok: false, reason: 'no_rule_match' };
}
